import React from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';
import { stringify } from 'qs';
import { FormGroup } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { reduxForm, getValues } from 'redux-form';

import TrustPilotBadge from '@src/components/TrustPilotBadge/TrustPilotBadge';
import {
    PERSONAL_INFORMATION_STEP_ID,
    convertActiveStepIdToStepPathName
} from '@src/containers/Routes/helper/stepActiveIds.helper';

import CompanySearch from './CompanySearch';
import fields from './fields';
import { nextStep, toggleLoadState, saveCustomerState } from '../../actions';
import CompliantFooterLinksDE from '../../components/ComplaintFooterLinksDE/CompliantFooterLinksDE';
import FinishStepButton from '../../components/FinishStepButton/FinishStepButton';
import FormField from '../../components/FormField/';
import FormStepHeading from '../../components/FormStepHeading/FormStepHeading';
import { buildApplicationState } from '../../helpers/applicationManager';
import { currencySymbols, localeCurrency } from '../../helpers/constants';
import { getAgeFromDateInMonths } from '../../helpers/dates';
import {
    numbersOnlyParser,
    intlAmountFormatterDecorator,
    numbersOnly,
    dateFormatter,
    getQueryParams
} from '../../helpers/helpers';
import normalizer from '../../helpers/inputNormalizer';
import months from '../../helpers/monthsDE';
import { scrollToErrors } from '../../helpers/scrollToErrors';
import {
    getCountryCode,
    hasAutomatedDecision
} from '../../lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import Tracking from '../../tracking/IwTracking';
import businessIntroductionMessages from '../BusinessIntroduction/BusinessIntroduction.messages';
import validator from '../BusinessIntroductionDE/validate';
import commonMessages from '../Containers.messages';
import { navigateTo } from '../Routes/Navigation.action';
import { getBasePath } from '../Routes/Navigation.selector';

const mapStateToProps = (state) => ({
    ui: state.ui,
    formValues: getValues(state.form.sign_up),
    application: state.fetched.application,
    hasAutomatedDecision: hasAutomatedDecision(state),
    currency: localeCurrency[getCountryCode(state)],
    country_code: getCountryCode(state),
    initialValues: state.fetched.application.payload,
    fetchCompaniesSuccess: state.fetched.companies.success, // implicitly passed to ./validate.js via redux-form for yup schema validation
    basePath: getBasePath(state)
});

const mapDispatchToProps = (dispatch) => ({
    nextStep: (step) => dispatch(nextStep(step)),
    toggleLoadState: () => dispatch(toggleLoadState()),
    saveCustomerState: (state) => dispatch(saveCustomerState(state)),
    trackLeadQuality: (stepName) =>
        dispatch(Tracking.trackLeadQualityAction(stepName)),
    navigateTo: (path) => dispatch(navigateTo(path))
});

class BusinessIntroductionDE extends React.Component {
    componentDidMount = () => {
        tracking.ampli.stepLoaded({
            'Step name': 'Business details',
            ...getAmplitudeDefaultEventProperties()
        });
    };

    getFinishButtonExplainerText = () => {
        const { hasAutomatedDecision } = this.props;

        if (hasAutomatedDecision)
            return 'Gute Nachrichten: Es sieht so aus, als könnte Sie Ihre Anfrage ohne Unterlagen stellen';

        return 'Der Antrag hat keinerlei Auswirkung auf Ihre Schufa-Bewertung';
    };

    /**
     * Method does PUT request with latest form/ui values in the background
     * and immediately navigates to the next step without waiting for the response
     */
    handleNextStepTransition = () => {
        const {
            formValues,
            application,
            ui,
            saveCustomerState,
            nextStep,
            trackLeadQuality,
            basePath,
            navigateTo
        } = this.props;
        const nextStepId = PERSONAL_INFORMATION_STEP_ID;
        const newState = buildApplicationState(application.payload, formValues);
        const stateToSave = {
            data: {
                application: newState,
                ui: _.merge({}, ui, {
                    activeStep: _.find(ui.steps, { id: nextStepId })
                })
            }
        };
        saveCustomerState(stateToSave);
        trackLeadQuality('businessIntroduction');

        tracking.ampli.businessDetailsSubmitted({
            ...getAmplitudeDefaultEventProperties()
        });

        const urlQueryParams = stringify(getQueryParams());
        nextStep(nextStepId);
        const pathname = convertActiveStepIdToStepPathName(nextStepId, false);

        navigateTo(
            `${basePath}/${pathname}${
                urlQueryParams ? `?${urlQueryParams}` : ''
            }`
        );
    };

    renderMonthOption = (option) => {
        return (
            <FormattedMessage defaultMessage={option.label} id={option.label} />
        );
    };

    UNSAFE_componentWillReceiveProps = (nextProps) => {
        const {
            fields: { company },
            values: {
                company: { registered, annual_turnover }
            }
        } = nextProps;
        // 'registered' is the company registration date
        if (getAgeFromDateInMonths(registered) < 0 && annual_turnover !== 0) {
            company.annual_turnover.onChange(0);
        }
    };

    render() {
        const {
            fields: { company },
            currency,
            handleSubmit,
            values: {
                company: { registered }
            },
            intl
        } = this.props;

        const annualTurnover = normalizer(
            company.annual_turnover,
            intlAmountFormatterDecorator(intl.formatNumber),
            numbersOnlyParser
        );
        const maskedYear = normalizer(company.registered.yy, numbersOnly, null);
        const companyAgeInMonths = getAgeFromDateInMonths(registered);
        const hasBusinessStarted = companyAgeInMonths >= 0;
        const doesBusinessHaveValidStartDate = dateFormatter(registered);
        const annualTurnoverLabel =
            doesBusinessHaveValidStartDate &&
            hasBusinessStarted &&
            companyAgeInMonths <= 12
                ? businessIntroductionMessages.turnoverSinceStartedTrading
                : businessIntroductionMessages.turnoverInLast12Months;
        const dateFields = [
            {
                type: 'select',
                field: company.registered.mm,
                className: 'border-right',
                selectAttributes: {
                    onChange: company.registered.mm.onChange,
                    optionRenderer: this.renderMonthOption,
                    valueRenderer: this.renderMonthOption,
                    value: company.registered.mm.value,
                    placeholder: 'Monat',
                    options: months
                }
            },
            {
                field: maskedYear,
                type: 'input',
                inputAttributes: {
                    maxLength: '4',
                    pattern: 'd*',
                    placeholder: 'JJJJ'
                }
            }
        ];

        return (
            <React.Fragment>
                <FormStepHeading>
                    <FormattedMessage
                        {...commonMessages.businessIntroduction}
                    />
                </FormStepHeading>
                <form
                    noValidate
                    autoComplete="off"
                    className="form-horizontal"
                    onSubmit={handleSubmit(this.handleNextStepTransition)}>
                    <div className="form-wrapper">
                        <FormGroup>
                            <CompanySearch companyField={company} />

                            <FormField.Header
                                message={
                                    <FormattedMessage
                                        {...businessIntroductionMessages.moreInformation}
                                    />
                                }
                            />

                            <FormField.Multiple
                                wrapperClass="company-registered"
                                label={
                                    <FormattedMessage
                                        {...businessIntroductionMessages.startedTradingDate}
                                    />
                                }
                                fields={dateFields}
                                tooltip={
                                    <FormattedMessage
                                        {...businessIntroductionMessages.startedTradingDateTooltip}
                                    />
                                }
                                onFocus={() => {
                                    tracking.ampli.formFieldStarted({
                                        form_field: 'trading_from_month'
                                    });
                                }}
                                onBlur={(event) => {
                                    if (event.target.value.length > 0) {
                                        tracking.ampli.formFieldCompleted({
                                            form_field: 'trading_from_month'
                                        });
                                    }
                                }}
                            />
                            <FormField
                                field={company.website}
                                label={
                                    <FormattedMessage
                                        {...businessIntroductionMessages.website}
                                    />
                                }
                                optional={true}>
                                <FormField.Input
                                    placeholder="www.mustermann.de"
                                    type="url"
                                    maxLength={190}
                                    parser={(w) => w.trim()}
                                />
                            </FormField>
                        </FormGroup>

                        <FormGroup>
                            <FormField
                                label={
                                    <FormattedMessage
                                        {...annualTurnoverLabel}
                                    />
                                }
                                field={annualTurnover}
                                tooltip={
                                    <FormattedMessage
                                        {...businessIntroductionMessages.turnoverTooltip}
                                    />
                                }>
                                <FormField.Input
                                    addon={true}
                                    addonPosition="right"
                                    addonContent={currencySymbols[currency]}
                                    disabled={!hasBusinessStarted}
                                    maxLength={14}
                                    placeholder={Number(250000).toLocaleString(
                                        'de-DE'
                                    )}
                                    pattern="\d*"
                                    whenFocus={() => {
                                        tracking.ampli.formFieldStarted({
                                            form_field: 'annual_turnover'
                                        });
                                    }}
                                    whenBlur={(event) => {
                                        if (event.target.value.length > 0) {
                                            tracking.ampli.formFieldCompleted({
                                                form_field: 'annual_turnover'
                                            });
                                        }
                                    }}
                                />
                            </FormField>
                        </FormGroup>
                    </div>

                    <FinishStepButton
                        type="submit"
                        onClick={() => scrollToErrors(this.props.errors)}
                        explainerText={this.getFinishButtonExplainerText()}>
                        Weiter zu Ihren persönlichen Angaben
                    </FinishStepButton>
                    <TrustPilotBadge />
                </form>
                <CompliantFooterLinksDE />
            </React.Fragment>
        );
    }
}

const reduxFormConfig = {
    form: 'sign_up',
    formKey: 'businessTypeFields',
    fields,
    asyncValidate: validator,
    asyncBlurFields: fields,
    alwaysAsyncValidate: true,
    getFormState: (state, reduxMountPoint) => state[reduxMountPoint],
    destroyOnUnmount: false
};

export default reduxForm(
    reduxFormConfig,
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(BusinessIntroductionDE));
